import { logWarning } from '~shared/util/logUtil'
import {
  KdsActionId,
  KdsActionPolicy,
  KdsConfigPermissionsResponse,
  toActionIds,
} from '../Types/KdsAuthorization'

/**
 * Legacy config never gated item availability or cancellation at all, and only exposed the
 * other two actions behind booleans -- mirror that exactly so an old device's behaviour
 * doesn't change.
 */
function legacyAvailableActions(config: KdsConfigPermissionsResponse): KdsActionId[] {
  const actions: KdsActionId[] = [
    KdsActionId.EditItemAvailability,
    KdsActionId.CancelCustomerOrders,
  ]
  if (config.canSetDepartmentAvailability) {
    actions.push(KdsActionId.SetDepartmentAvailability)
  }
  if (config.canAddKdsOrders) {
    actions.push(KdsActionId.AddKdsOrders)
  }
  return actions
}

/**
 * Requires both `userControlsLabEnabled` AND at least one permissions array present -- a
 * department can have the lab on before permissions are configured, and undefined arrays there
 * mean "not configured yet", so it must fall back to the legacy booleans rather than reading
 * as "no actions available".
 */
export function normalizeKdsConfig(config: KdsConfigPermissionsResponse): KdsActionPolicy {
  const hasPermissionArrays =
    config.permissions !== undefined || config.elevatedPermissions !== undefined
  const permissionsLabEnabled = config.userControlsLabEnabled === true && hasPermissionArrays

  const availableActions = permissionsLabEnabled
    ? toActionIds(config.permissions)
    : legacyAvailableActions(config)
  const rawElevatedActions = permissionsLabEnabled ? toActionIds(config.elevatedPermissions) : []

  const elevatedOnly = rawElevatedActions.filter(actionId => !availableActions.includes(actionId))
  if (elevatedOnly.length > 0) {
    // Action IDs only -- never log PIN/token data.
    logWarning(
      'kdsActionPolicy.normalizeKdsConfig',
      'Action(s) are elevated but not available; treating as unavailable',
      { actionIds: elevatedOnly.join(',') }
    )
  }

  // Drop them here too, not just in isActionElevated's guard, so a consumer reading
  // elevatedActions directly can't observe an action that isn't in availableActions.
  const elevatedActions = rawElevatedActions.filter(actionId => availableActions.includes(actionId))

  return { availableActions, elevatedActions }
}

/** True when the device's config grants this action at all (device-token or elevated). */
export function isActionAvailable(
  policy: KdsActionPolicy | undefined,
  actionId: KdsActionId
): boolean {
  return policy?.availableActions.includes(actionId) ?? false
}

/** False whenever the action isn't available, even if it's listed in `elevatedActions`. */
export function isActionElevated(
  policy: KdsActionPolicy | undefined,
  actionId: KdsActionId
): boolean {
  return (
    isActionAvailable(policy, actionId) && (policy?.elevatedActions.includes(actionId) ?? false)
  )
}

export function getActionAvailability(
  policy: KdsActionPolicy | undefined,
  actionId: KdsActionId
): { available: boolean; elevated: boolean } {
  return {
    available: isActionAvailable(policy, actionId),
    elevated: isActionElevated(policy, actionId),
  }
}
